import React, { useMemo, useRef } from 'react'
import styled from 'styled-components'
import { useTranslation } from 'react-i18next'
import * as Layout from 'views/layouts/MainLayout/MainLayout'
import * as Header from 'views/ui/Header/Header'
import FarmerOrderSkuList from './FarmerOrderSkuList'
import FarmerOrderSkuActions from 'modules/domain/farmerOrderSku/duck'
import FarmerOrderSkuRoutes from 'views/pages/FarmerOrderSku/routes'
import { usePermissions, CAPABILITY, PERMISSION } from 'modules/permissions/permissions'
import { AddButton, AdvancedSearchField, useAction, useHelmet } from '@agro-club/frontend-shared'
import { FarmerOrderSkuListSearchOption } from 'modules/domain/farmerOrderSku/types'
import useFeatureFlags from 'hooks/featureFlags/useFeatureFlags'
import { useSelector } from 'react-redux'
import AuthSelectors from 'modules/domain/auth/selectors'
import { Actions, FeatureFlagModifiers, Filters, Sections, isDistributor } from 'types/entities'
import SeasonSelectors from 'modules/domain/season/selectors'
import { useSeasonList } from 'modules/domain/season/hooks'
import { Progress } from 'modules/types'
import { SpinnerWrapper } from 'views/components/CommonTableComponents/CommonTableComponents'
import { generateActionAccessString, generateCrmFilterAccessString } from 'modules/utils/generateStringHelpers'
import SpinnerLayout from 'views/layouts/SpinnerLayout/SpinnerLayout'
import FarmerOrderSkuSelectors from 'modules/domain/farmerOrderSku/selectors'

const SearchInputWrapper = styled.div`
  margin-top: 14px;
  margin-bottom: 4px;
  width: 50%;
`

const DEFAULT_SEARCH_OPTION = 'slug'

const FarmerOrderSkuTable: React.FC = () => {
  const filter = useSelector(FarmerOrderSkuSelectors.filter)
  const filterUpdated = useAction(FarmerOrderSkuActions.filterUpdated)
  const { t } = useTranslation(['farmerOrder', 'common'])
  const checkFeatureFlag = useFeatureFlags()
  const prevOption = useRef(DEFAULT_SEARCH_OPTION)
  const role = useSelector(AuthSelectors.role)
  const [seasonsProgress] = useSeasonList()
  const isHouseholdFilterOptionHidden = checkFeatureFlag(
    generateCrmFilterAccessString(Sections.FarmerOrders, Filters.Household, FeatureFlagModifiers.Hidden),
  )

  const handleSearchChange = React.useCallback(
    (search: string, option: string) => {
      if (!(option !== prevOption.current && !search)) {
        filterUpdated({
          ...filter,
          search: search ? search : undefined,
          search_by: option as FarmerOrderSkuListSearchOption,
        })
      }
      prevOption.current = option
    },
    [filter, filterUpdated],
  )

  useHelmet({ title: t('ordersMetaTitle') })

  const searchOptions: Array<{
    id: FarmerOrderSkuListSearchOption
    title: string
    placeholder: string
  }> = useMemo(() => {
    const arr: Array<FarmerOrderSkuListSearchOption> = ['slug', 'user', 'distributor', 'producer', 'product']

    if (!isHouseholdFilterOptionHidden) arr.push('household')

    return arr.map((item) => ({
      id: item,
      title: t(`list.searchOptions.${item}.title`),
      placeholder: t(`list.searchOptions.${item}.placeholder`),
    }))
  }, [t, isHouseholdFilterOptionHidden])

  const currentSeason = useSelector(SeasonSelectors.currentSeason)

  const isCreatePermitted =
    usePermissions({ capability: CAPABILITY.SKU_ORDERS, permission: PERMISSION.C }) &&
    ((isDistributor(role) && !currentSeason?.stop_taking_orders) || !isDistributor(role)) &&
    !checkFeatureFlag(
      generateActionAccessString(Sections.FarmerOrders, Actions.Create, FeatureFlagModifiers.Restricted),
    )

  return (
    <>
      <Layout.Header>
        <Header.Root>
          <Header.Title title={`${t('list.heading')}`}>
            {isCreatePermitted && (
              <AddButton data-test-id={'button-add'} to={FarmerOrderSkuRoutes.Add}>
                {t('common:addNew')}
              </AddButton>
            )}
          </Header.Title>
          <SearchInputWrapper>
            <AdvancedSearchField
              options={searchOptions}
              onChange={handleSearchChange}
              defaultOption={filter.search_by || DEFAULT_SEARCH_OPTION}
              value={filter.search}
            />
          </SearchInputWrapper>
        </Header.Root>
      </Layout.Header>
      <Layout.Content>
        {seasonsProgress === Progress.SUCCESS ? (
          <FarmerOrderSkuList />
        ) : (
          <SpinnerWrapper>
            <SpinnerLayout />
          </SpinnerWrapper>
        )}
      </Layout.Content>
    </>
  )
}

export default FarmerOrderSkuTable
